import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { money } from './promotionsData';
import './ApplyPromotionModal.scss';
import {
  orderPromotionApply,
  orderPromotionsLoad,
  orderPromotionsReset,
  orderPromotionPreviewLoad,
  orderPromotionPreviewClear,
} from '../../../../../redux/actions/order/orderPromotionActions';
import { orderLoad } from '../../../../../redux/actions/order/orderActions';

function formatDiscountLabel(preview) {
  if (!preview) return '—';
  const value = preview.configuration?.discount_value;
  const type = preview.promotion_type;

  if (type === 'percent_off' && value != null) {
    return `${value}%`;
  }
  if ((type === 'amount_off' || type === 'dollar_off' || type === 'fixed_amount') && value != null) {
    return money(value);
  }
  if (preview.rule_summary) {
    return preview.rule_summary;
  }
  return value != null ? String(value) : '—';
}

function mapPromotion(p) {
  const isEligible =
    p.eligibile !== undefined
      ? Boolean(p.eligibile)
      : p.eligible !== undefined
      ? Boolean(p.eligible)
      : p.isEligible !== undefined
      ? Boolean(p.isEligible)
      : p.all_conditions_pass !== undefined
      ? Boolean(p.all_conditions_pass)
      : true;

  return {
    id: p.id,
    name: p.name || 'Unnamed Promotion',
    code: p.code ? (p.code.startsWith('POS ') ? p.code : `POS ${p.code}`) : '',
    rawCode: p.code || '',
    rule: p.rule_summary || (p.promotion_type ? `${p.promotion_type} discount` : ''),
    rule_summary: p.rule_summary || '',
    applies_to: p.applies_to || 'all',
    isEligible,
    status: isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE',
    statusType: isEligible ? 'good' : 'bad',
    raw: p,
  };
}

function ApplyPromotionModal({
  isOpen,
  onClose,
  onApply,
  order = {},
  promotions = [],
  promoLoading,
  meta = {},
  preview,
  previewLoading,
  orderPromotionsLoad: loadPromotions,
  orderPromotionsReset: resetPromotions,
  orderPromotionPreviewLoad: loadPreview,
  orderPromotionApply: applyPromotion,
  orderLoad: loadOrder,
  orderPromotionPreviewClear: clearPreview,
  storeId,
}) {
  const [selectedPromoId, setSelectedPromoId] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [page, setPage] = useState(1);

  const promoLeftRef = useRef(null);
  const observerTargetRef = useRef(null);

  const pageRef = useRef(1);
  pageRef.current = page;

  const loadingRef = useRef(false);
  loadingRef.current = promoLoading;

  const hasMore = useMemo(() => {
    if (meta?.pagination?.next != null) {
      return true;
    }
    if (meta?.pagination?.pages != null) {
      return page < meta.pagination.pages;
    }
    if (meta?.pagination?.count != null) {
      return promotions.length < meta.pagination.count;
    }
    return false;
  }, [meta, page, promotions.length]);

  const hasMoreRef = useRef(false);
  hasMoreRef.current = hasMore;

  useEffect(() => {
    if (!isOpen || !order?.id) return;
    setPage(1);
    pageRef.current = 1;
    loadPromotions(order.id, {
      rpp: 10,
      page: 1,
      sort: 'created_at',
      direction: 'desc',
      filter: { store_id: storeId, is_visible: true, status: 'active' },
    });
  }, [isOpen, order?.id, loadPromotions, storeId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPromoId(null);
      clearPreview();
      if (resetPromotions) {
        resetPromotions();
      }
      setPage(1);
      pageRef.current = 1;
    }
  }, [isOpen, clearPreview, resetPromotions]);

  const handleLoadMore = useCallback(() => {
    if (loadingRef.current || !hasMoreRef.current || !order?.id) return;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    setPage(nextPage);
    loadPromotions(order.id, {
      rpp: 10,
      page: nextPage,
      sort: 'created_at',
      direction: 'desc',
      filter: { store_id: storeId, is_visible: true, status: 'active' },
    });
  }, [order?.id, loadPromotions, storeId]);

  useEffect(() => {
    if (!isOpen || !hasMore) return;
    const target = observerTargetRef.current;
    const root = promoLeftRef.current;
    if (!target || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [isOpen, hasMore, handleLoadMore]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= 60) {
        handleLoadMore();
      }
    },
    [handleLoadMore]
  );

  useEffect(() => {
    if (!isOpen || !order?.id || selectedPromoId == null) return;
    loadPreview(order.id, selectedPromoId);
  }, [isOpen, order?.id, selectedPromoId, loadPreview]);

  const promotionsList = useMemo(() => {
    if (!Array.isArray(promotions)) return [];
    return promotions.map(mapPromotion);
  }, [promotions]);

  const eligibleCount = useMemo(
    () => promotionsList.filter((p) => p.isEligible).length,
    [promotionsList]
  );

  const totalCount =
    meta?.pagination?.count != null ? meta.pagination.count : promotionsList.length;

  // Auto-select initial eligible promotion; never override user selection
  useEffect(() => {
    if (!isOpen || promotionsList.length === 0) return;

    if (selectedPromoId !== null && selectedPromoId !== undefined) {
      const exists = promotionsList.some((p) => String(p.id) === String(selectedPromoId));
      if (exists) return;
    }

    const existingPromoId = order?.orders_promotion?.settings_promotion_id;
    const existingPromo =
      existingPromoId &&
      promotionsList.find(
        (p) => String(p.id) === String(existingPromoId) && p.isEligible
      );

    if (existingPromo) {
      setSelectedPromoId(existingPromo.id);
      return;
    }

    const firstEligible = promotionsList.find((p) => p.isEligible);
    if (firstEligible) {
      setSelectedPromoId(firstEligible.id);
    } else if (promotionsList.length > 0) {
      setSelectedPromoId(promotionsList[0].id);
    }
  }, [isOpen, promotionsList, order?.orders_promotion?.settings_promotion_id, selectedPromoId]);

  const selectedPromo = useMemo(() => {
    if (selectedPromoId !== null && selectedPromoId !== undefined) {
      const match = promotionsList.find((p) => String(p.id) === String(selectedPromoId));
      if (match) return match;
    }
    return promotionsList.find((p) => p.isEligible) || promotionsList[0] || null;
  }, [promotionsList, selectedPromoId]);

  // Preview comes only from Redux API response
  const previewData = useMemo(() => {
    if (!preview || String(preview.id) !== String(selectedPromoId)) return null;

    return {
      name: preview.name || selectedPromo?.name || 'Promotion',
      retail: preview.order_total,
      discountLabel: formatDiscountLabel(preview),
      savings: preview.discount,
      total: preview.remaining,
      rule: preview.notes || preview.rule_summary || '',
    };
  }, [preview, selectedPromoId, selectedPromo]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && isOpen && !isApplying) {
        onClose();
      }
    },
    [isOpen, isApplying, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleCardClick = (promo) => {
    if (!promo.isEligible && promo.statusType !== 'info') {
      return;
    }
    setSelectedPromoId(promo.id);
  };

  const handleApplyCommit = async () => {
    if (!selectedPromo || !selectedPromo.isEligible) return;
    setIsApplying(true);
    try {
      if (order?.id) {
        const promoAttrs = {
          orders_order_id: order.id,
          settings_promotion_id: selectedPromo.id,
          configuration: preview?.configuration,
        };
        if (order?.orders_promotion?.id) {
          promoAttrs.id = order.orders_promotion.id;
        }

        const payload = {
          order: {
            orders_promotion_attributes: promoAttrs,
          },
        };

        await applyPromotion(order.id, payload, { include: '*' });
        await loadOrder(order.id, { include: '*' });
      }
      if (onApply) {
        onApply(selectedPromo);
      }
      onClose();
    } catch (err) {
      // Errors handled via Toastr in reducer
    } finally {
      setIsApplying(false);
    }
  };

  return createPortal(
    <div
      className="promo-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isApplying) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="promo-modal">
        <div className="promo-modal-head">
          <div>
            <h4 className="promo-modal-title">Apply Promotion</h4>
          </div>
          <button
            type="button"
            className="close-x"
            onClick={onClose}
            aria-label="Close"
            disabled={isApplying}
          >
            ×
          </button>
        </div>

        <div className="promo-modal-body">
          <div
            className="promo-left"
            ref={promoLeftRef}
            onScroll={handleScroll}
          >
            <div className="modal-section-label">Configured Promotions</div>
            <div className="promo-count">
              {totalCount} configured promotion{totalCount === 1 ? '' : 's'} • {eligibleCount}{' '}
              currently eligible for this order
            </div>

            {promoLoading && promotionsList.length === 0 ? (
              <div className="text-center py-5">
                <i className="la la-spinner la-spin la-2x text-primary" />
                <div className="mt-2 text-muted font-size-13">Loading promotions...</div>
              </div>
            ) : promotionsList.length === 0 ? (
              <div className="text-center py-5 text-muted font-size-14">
                No promotions configured or eligible for this order.
              </div>
            ) : (
              <div className="promo-list">
                {promotionsList.map((promo) => {
                  const isSelected =
                    selectedPromo && String(selectedPromo.id) === String(promo.id);
                  const isDisabled = !promo.isEligible && promo.statusType !== 'info';
                  return (
                    <div
                      key={promo.id}
                      className={`promo-card ${isSelected ? 'selected' : ''} ${
                        isDisabled ? 'disabled' : ''
                      }`}
                      onClick={() => handleCardClick(promo)}
                      role="button"
                      tabIndex={isDisabled ? -1 : 0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleCardClick(promo);
                        }
                      }}
                    >
                      <div className="radio" />
                      <div className="promo-card-content">
                        <div className="d-flex align-items-center flex-wrap">
                          <span className="promo-name">{promo.name}</span>
                          {promo.code && <span className="promo-code ml-2">{promo.code}</span>}
                          {promo.applies_to && promo.applies_to !== 'all' && (
                            <span className="badge badge-light border ml-2 text-uppercase font-size-10">
                              {promo.applies_to}
                            </span>
                          )}
                        </div>
                        {promo.rule && <div className="promo-rule">{promo.rule}</div>}
                      </div>
                      <span className={`status ${promo.statusType || 'good'}`}>
                        {promo.status}
                      </span>
                    </div>
                  );
                })}

                {promoLoading && promotionsList.length > 0 && (
                  <div className="text-center py-3">
                    <i className="la la-spinner la-spin mr-1 text-primary" />
                    <span className="text-muted font-size-12">Loading more promotions...</span>
                  </div>
                )}

                {hasMore && (
                  <div
                    ref={observerTargetRef}
                    className="promo-scroll-sentinel"
                    style={{ height: '1px' }}
                  />
                )}

                {!hasMore && promotionsList.length >= 10 && (
                  <div className="text-center py-2 text-muted font-size-11">
                    All promotions loaded
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="promo-right">
            <div className="modal-section-label">Promotion Preview</div>

            {previewLoading && !previewData ? (
              <div className="text-center py-5">
                <i className="la la-spinner la-spin la-2x text-primary" />
                <div className="mt-2 text-muted font-size-13">Loading preview...</div>
              </div>
            ) : !previewData ? (
              <div className="text-center py-5 text-muted font-size-14">
                Select a promotion to preview pricing.
              </div>
            ) : (
              <div className={`preview-wrap ${previewLoading ? 'is-loading' : ''}`}>
                <div className="preview-panel">
                  <div className="preview-header">{previewData.name}</div>

                  <div className="preview-lines">
                    <div className="preview-line">
                      <span>Retail order</span>
                      <span className="preview-value">{money(previewData.retail)}</span>
                    </div>
                    <div className="preview-line muted">
                      <span>Eligible promotion discount</span>
                      <span>{previewData.discountLabel}</span>
                    </div>
                    <div className="preview-line discount">
                      <span>Promotion savings</span>
                      <span className="preview-value">-{money(previewData.savings)}</span>
                    </div>
                  </div>

                  <div className="preview-total">
                    <span>Patient Total</span>
                    <span className="preview-total-value">{money(previewData.total)}</span>
                  </div>
                </div>

                {previewData.rule && (
                  <div className="preview-rule">{previewData.rule}</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="promo-modal-foot">
          <div className="foot-note">
            Pricing preview updates before the promotion is committed to the order.
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={onClose}
              disabled={isApplying}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleApplyCommit}
              disabled={isApplying || !selectedPromo || !selectedPromo.isEligible}
            >
              {isApplying ? (
                <>
                  <i className="la la-spinner la-spin mr-1" />
                  Applying...
                </>
              ) : (
                'Apply Promotion'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

ApplyPromotionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  order: PropTypes.object,
  promotions: PropTypes.array,
  promoLoading: PropTypes.bool,
  meta: PropTypes.object,
  preview: PropTypes.object,
  previewLoading: PropTypes.bool,
  orderPromotionsLoad: PropTypes.func.isRequired,
  orderPromotionsReset: PropTypes.func,
  orderPromotionPreviewLoad: PropTypes.func.isRequired,
  orderPromotionApply: PropTypes.func.isRequired,
  orderLoad: PropTypes.func.isRequired,
  orderPromotionPreviewClear: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const orderPromotion = state.orderPromotion || {};
  const storeId = state.order.order.store_id;
  return {
    promotions: orderPromotion.promotions || [],
    promoLoading: orderPromotion.loading || false,
    meta: orderPromotion.meta || {},
    preview: orderPromotion.preview || null,
    previewLoading: orderPromotion.previewLoading || false,
    storeId,
  };
};

const mapDispatchToProps = {
  orderPromotionsLoad,
  orderPromotionsReset,
  orderPromotionPreviewLoad,
  orderPromotionApply,
  orderLoad,
  orderPromotionPreviewClear,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApplyPromotionModal);
